import {
  ActionIcon,
  Avatar,
  Box,
  Burger,
  Drawer,
  Group,
  Menu,
  Text,
  Tooltip,
  UnstyledButton,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Spotlight, spotlight, type SpotlightActionData } from "@mantine/spotlight";
import {
  IconChevronLeft,
  IconHelp,
  IconLogout,
  IconMoon,
  IconSearch,
  IconSun,
} from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink as RouterNavLink, Outlet, useLocation, useNavigate } from "react-router";
import {
  AccountItemText,
  CountBadge,
  HOW_LOGO_URL,
  isNavItemActive,
  type AppFrameAccount,
  type AppFrameNavItem,
} from "@how/ui";
import "./sidebar-frame.css";

/** Where the preview lives: every page of the dashboard again, under /sidebar, with this sidebar instead of AppFrame's. */
export const SIDEBAR_PREVIEW_BASE = "/sidebar";

/** Whether a path is inside the preview. */
export function isSidebarPreviewPath(pathname: string): boolean {
  return pathname === SIDEBAR_PREVIEW_BASE || pathname.startsWith(`${SIDEBAR_PREVIEW_BASE}/`);
}

/** A navigation item moved under /sidebar, so clicking around stays in the preview. Home matches only itself. */
export function toSidebarPreviewItem<T extends AppFrameNavItem>(item: T): T {
  return item.to === "/"
    ? { ...item, to: SIDEBAR_PREVIEW_BASE, end: true }
    : { ...item, to: `${SIDEBAR_PREVIEW_BASE}${item.to}` };
}

export interface SidebarFrameProps {
  readonly churchName?: string | undefined;
  /** Navigation is convenience, never authorization: filter items before passing them. */
  readonly nav: readonly AppFrameNavItem[];
  readonly account: AppFrameAccount;
}

/**
 * The sidebar preview (2026-10-13): one floating panel that holds the brand, search, the menu and the signed-in person
 * with sign-out, and folds down to a rail of icons. The colour-scheme toggle and the account menu stay at the top
 * right, where the old frame had them. Brand blue marks the selected row with a lit rail at the panel's edge. On a phone it
 * is a drawer behind a menu button. Same navigation, account and Spotlight as AppFrame; only the frame differs.
 */
export function SidebarFrame({ churchName, nav, account }: SidebarFrameProps) {
  // Not remembered across reloads: the staff dashboard keeps browser storage per church only (church-isolation.test).
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpened, drawer] = useDisclosure(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const spotlightActions: SpotlightActionData[] = [
    ...nav.map((item) => ({
      id: item.to,
      label: item.label,
      description: `Go to ${item.label}`,
      onClick: () => {
        void navigate(item.to);
      },
    })),
    ...(account.helpUrl === undefined
      ? []
      : [
          {
            id: "how-help-center",
            label: "Help center",
            description: "Step-by-step answers, in a new tab",
            onClick: () => {
              window.open(account.helpUrl, "_blank", "noopener");
            },
          },
        ]),
  ];
  const panel = { churchName, nav, account, pathname };

  return (
    <div className="how-sb-frame" data-collapsed={collapsed || undefined}>
      <header className="how-sb-topbar">
        <Burger
          opened={drawerOpened}
          onClick={drawer.toggle}
          size="sm"
          aria-label="Toggle navigation"
        />
        <BrandMark />
        <UnstyledButton
          className="how-sb-topbar-search"
          aria-label="Open search"
          onClick={spotlight.open}
        >
          <IconSearch size={18} stroke={1.8} />
        </UnstyledButton>
        <HeaderTools account={account} />
      </header>
      <aside className="how-sb">
        <SidebarPanel
          {...panel}
          collapsed={collapsed}
          onToggle={() => {
            setCollapsed((was) => !was);
          }}
        />
      </aside>
      <Drawer
        opened={drawerOpened}
        onClose={drawer.close}
        size={288}
        withCloseButton={false}
        padding={0}
        classNames={{ content: "how-sb-drawer", body: "how-sb-drawer-body" }}
      >
        <SidebarPanel {...panel} collapsed={false} onNavigate={drawer.close} />
      </Drawer>
      <main className="how-sb-main">
        <div className="how-sb-header">
          <HeaderTools account={account} />
        </div>
        {/* Keyed to the route so each page arrives with the same short rise (.how-page-enter). */}
        <Box key={pathname} className="how-page-enter how-content">
          <Outlet />
        </Box>
      </main>
      <Spotlight
        actions={spotlightActions}
        shortcut={["mod + K"]}
        nothingFound="Nothing found"
        searchProps={{ placeholder: "Search" }}
      />
    </div>
  );
}

interface SidebarPanelProps extends SidebarFrameProps {
  readonly pathname: string;
  readonly collapsed: boolean;
  /** The edge button that folds the panel; absent in the phone's drawer, which is always open wide. */
  readonly onToggle?: () => void;
  /** Called after a link is followed (closes the drawer). */
  readonly onNavigate?: () => void;
}

function SidebarPanel({
  churchName,
  nav,
  account,
  pathname,
  collapsed,
  onToggle,
  onNavigate,
}: SidebarPanelProps) {
  const edges = useScrollEdges();
  return (
    <div className="how-sb-panel" data-collapsed={collapsed || undefined}>
      {onToggle === undefined ? null : (
        <UnstyledButton
          className="how-sb-toggle"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          onClick={onToggle}
        >
          <IconChevronLeft size={14} stroke={2.4} aria-hidden />
        </UnstyledButton>
      )}

      <div className="how-sb-brand">
        <BrandMark />
        <div className="how-sb-fade how-sb-brand-text">
          <Text className="how-sb-title" truncate>
            {churchName ?? "HoW"}
          </Text>
          <Text className="how-sb-subtitle" truncate>
            Staff dashboard
          </Text>
        </div>
      </div>

      <Hint label="Search" collapsed={collapsed}>
        <UnstyledButton className="how-sb-search" aria-label="Open search" onClick={spotlight.open}>
          <IconSearch size={18} stroke={1.8} aria-hidden className="how-sb-search-icon" />
          <span className="how-sb-fade how-sb-search-label">Search</span>
        </UnstyledButton>
      </Hint>

      <div className="how-sb-section" aria-hidden>
        Menu
      </div>

      <nav
        ref={edges.ref}
        className="how-sb-nav"
        aria-label="Main"
        data-more-above={edges.above || undefined}
        data-more-below={edges.below || undefined}
        onScroll={edges.update}
      >
        {nav.map((item) => {
          const count = item.count ?? 0;
          return (
            <Hint key={item.to} label={item.label} collapsed={collapsed}>
              <RouterNavLink
                to={item.to}
                end={item.end ?? item.to === "/"}
                className="how-sb-link"
                data-active={isNavItemActive(pathname, item) || undefined}
                // Visible words first (SC 2.5.3): "Communications, 3 unread".
                aria-label={
                  count > 0
                    ? `${item.label}, ${String(count)}${item.countLabel === undefined ? "" : ` ${item.countLabel}`}`
                    : undefined
                }
                onClick={onNavigate}
              >
                <span className="how-sb-link-icon">
                  <item.icon size={19} stroke={1.7} />
                  {count > 0 ? <span className="how-sb-dot" aria-hidden /> : null}
                </span>
                <span className="how-sb-fade how-sb-link-label">{item.label}</span>
                {count > 0 ? (
                  <span className="how-sb-fade how-sb-link-count">
                    <CountBadge count={count} />
                  </span>
                ) : null}
              </RouterNavLink>
            </Hint>
          );
        })}
      </nav>

      {/* The signed-in person, pinned to the foot, with sign-out one click away: what the old sidebar held. */}
      <div className="how-sb-foot">
        <div className="how-sb-account">
          <Avatar
            size={30}
            radius="xl"
            name={account.name?.trim() || account.email}
            color="brand"
            variant="filled"
            className="how-sb-avatar"
          />
          <span className="how-sb-fade how-sb-account-text">
            <Text span size="sm" fw={600} truncate display="block">
              {account.email.split("@")[0]}
            </Text>
            <Text span size="xs" c="dimmed" truncate display="block">
              {account.label ?? account.email}
            </Text>
          </span>
          <ActionIcon
            variant="subtle"
            color="gray"
            radius="xl"
            aria-label="Sign out"
            className="how-sb-fade"
            onClick={account.onSignOut}
          >
            <IconLogout size={18} />
          </ActionIcon>
        </div>
      </div>
    </div>
  );
}

/**
 * The top right of the page, as in the old frame: the colour-scheme toggle and the account menu. Search moved into
 * the sidebar, so it is not repeated here.
 */
function HeaderTools({ account }: { readonly account: AppFrameAccount }) {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  return (
    <Group gap="xs" wrap="nowrap">
      <Tooltip label={colorScheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label="Toggle color scheme"
          onClick={toggleColorScheme}
        >
          {colorScheme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
        </ActionIcon>
      </Tooltip>
      <Menu position="bottom-end" width={240}>
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray" radius="xl" aria-label="Account menu">
            <Avatar
              size="sm"
              radius="xl"
              name={account.name?.trim() || account.email}
              color="brand"
              variant="filled"
            />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>
            <Text size="xs" truncate>
              {account.email}
            </Text>
          </Menu.Label>
          {(account.links ?? []).map((link) => {
            const shared = {
              leftSection: <link.icon size={16} />,
              "aria-label":
                link.description === undefined ? undefined : `${link.label}, ${link.description}`,
              children: <AccountItemText label={link.label} description={link.description} />,
            };
            return "onSelect" in link ? (
              <Menu.Item key={link.label} onClick={link.onSelect} {...shared} />
            ) : "href" in link ? (
              <Menu.Item key={link.href} component="a" href={link.href} {...shared} />
            ) : (
              <Menu.Item key={link.to} component={Link} to={link.to} {...shared} />
            );
          })}
          {account.helpUrl === undefined ? null : (
            <Menu.Item
              component="a"
              href={account.helpUrl}
              target="_blank"
              rel="noopener"
              aria-label="Help center (opens in a new tab)"
              leftSection={<IconHelp size={16} />}
            >
              Help center
            </Menu.Item>
          )}
          <Menu.Divider />
          <Menu.Item leftSection={<IconLogout size={16} />} onClick={account.onSignOut}>
            Sign out
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}

/** Whether the menu runs past its top or bottom edge, so only an edge with more past it fades. */
function useScrollEdges() {
  const ref = useRef<HTMLElement>(null);
  const [edges, setEdges] = useState({ above: false, below: false });
  const update = useCallback(() => {
    const el = ref.current;
    if (el === null) return;
    const above = el.scrollTop > 1;
    const below = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setEdges((prev) => (prev.above === above && prev.below === below ? prev : { above, below }));
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [update]);
  return { ref, ...edges, update };
}

/** The HoW mark in a blue disc: the script logo as a white mask, like the header's. */
function BrandMark() {
  return (
    <span className="how-sb-mark">
      <Box
        role="img"
        aria-label="HoW"
        className="how-sb-mark-logo"
        style={{ "--how-shell-logo": `url("${HOW_LOGO_URL}")` }}
      />
    </span>
  );
}

/** A label beside a control while the panel is folded to icons; nothing when it is open and the words show. */
function Hint({
  label,
  collapsed,
  children,
}: {
  readonly label: string;
  readonly collapsed: boolean;
  readonly children: ReactNode;
}) {
  return (
    <Tooltip
      label={label}
      position="right"
      offset={18}
      withArrow
      openDelay={0}
      disabled={!collapsed}
      classNames={{ tooltip: "how-floating how-sb-tip" }}
    >
      {children}
    </Tooltip>
  );
}
